import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { MovementWaterfall, SalesFunnel, BarByStage, ReceivablesAging, SourceDonut }
  from './CeoCharts';
import AffiliatePanel from './AffiliatePanel';
import OverviewCard, { StateBody } from './_OverviewCard';
import GoalTracker from './GoalTracker';
import { C, S, FORECAST_DASH } from './_overviewTheme';
import { ChartWell, PanelActions, useChartView } from './_PanelChrome';
import { stagesToBarRows, agingToBuckets, invoiceBucketsToStack, leadSourcesToRows }
  from './_panelAdapters';
import { formatCents, formatDollars } from './_money';
import { deltaPct, mergeRepRows, financeCards, overdueRowsFromAttention, withTargetCents, affiliateSubLine } from './_overviewData';
import { customRange } from '../../lib/easyReports';
import {
  metric, cardState, reasonText, previousLabel, unmappedStages,
  financeMetric, financeRangeLabel, financePreviousLabel, previousReasonText, pointInTimeNote,
} from './_ceoMetrics';

/**
 * ⛔⛔ THE CEO OVERVIEW — Card P145, built to the mockup Daniel approved 28 Oct.
 *
 * ── WHY A NEW SCREEN AND NOT MORE TILES ────────────────────────────────────
 * The existing CeoDashboard is a correct list of figures in the order they
 * were implemented. This is the order a CEO reads: what happened (KPIs), what
 * it is doing (trend), what state the work is in (status), what needs a
 * person (exceptions), what is owed (receivables).
 *
 * ── ⛔⛔ EVERY CARD HAS THREE STATES ────────────────────────────────────────
 * number / loading / "not tracked yet" with the reason. Never 0 for absent.
 * Measured on the live dashboard 28 Oct: "Therapies scheduled 0",
 * "Lead → Therapy 0%", "60-day forecast $0" — none of those were zero, they
 * were broken, and nothing on screen distinguished the two.
 *
 * ── WHAT THIS SCREEN DOES NOT OWN ──────────────────────────────────────────
 * ⚠ It writes NO queries. Active-pipeline, the three zeros and the period
 * parameter are W3's (routes/_ceo_dashboard.js, _funnel_report.js, the
 * forecast); the payments numbers are W2's (reports:finance:summary, #727).
 * This file consumes those channels and renders. When W3's period-aware
 * payload lands, `period` flows through unchanged — the props are already
 * shaped for it.
 */

const MONEY_SUB = 'Payments that succeeded, less refunds.';

/*
 * ⛔⛔ P179 item 5 — EVERY PANEL SCROLLS ON ITS OWN. Measured on the live
 * dashboard 30 Oct: no panel on this screen carried `overflow-y` at all, so a
 * panel with more content than height either stretched the page or had its
 * rows cut off with nothing to say so. Two panels were additionally capped in
 * code — exceptions at 5 rows and active pipeline at 6 — which is the worse
 * failure: the rows were never rendered, so no amount of scrolling could
 * reveal them and the panel looked complete.
 *
 * ⚠ `overscrollBehavior: 'contain'` ONLY HERE, not on the page. It stops a
 * wheel gesture that reaches the end of this panel from continuing to scroll
 * the page underneath — scroll chaining, which feels like the panel "sticking".
 * It does NOT trap the gesture: once the pointer leaves the panel the page
 * scrolls normally, and a panel shorter than its max height never scrolls at
 * all because `auto` shows no scrollbar it does not need.
 */
const SCROLL_BODY = Object.freeze({
  maxHeight: 360,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  // ⚠ Momentum scrolling on iOS; harmless elsewhere.
  WebkitOverflowScrolling: 'touch',
});

/**
 * ⚠ P185 — `notAffected` LABELS A PANEL THE DATE RANGE CANNOT REACH.
 *
 * The card's rule: anything that cannot honour a custom range must SAY so.
 * A panel that silently ignores the window the user just chose is the worst
 * shape — it shows real numbers for a different question, and nothing on
 * screen distinguishes that from numbers that followed the range.
 */
function Panel({ title, action, onAction, children, testid, state, reason, scroll = true, notAffected = null }) {
  return (
    <section style={S.card} data-testid={testid}>
      <div style={S.cardHead}>
        <h2 style={S.h2}>{title}</h2>
        {/*
          * ⛔⛔ NO HANDLER ⇒ NO LINK. A "View →" with nothing behind it is the
          * v2.1.12 defect in its purest form: the affordance promises a
          * destination and the click does nothing, which reads to the user as
          * a broken product rather than an absent feature. If the page cannot
          * supply a target, the honest rendering is no link at all.
          */}
        {action && typeof onAction === 'function' ? (
          <button type="button" style={S.link} onClick={onAction}>{action}</button>
        ) : null}
      </div>
      {notAffected && (
        <div data-testid={testid ? `${testid}-not-affected` : undefined}
             style={{ fontSize: 11, color: C.muted, padding: '0 16px 6px' }}>
          Not affected by date range — {notAffected}
        </div>
      )}
      {scroll ? (
        <div style={SCROLL_BODY} data-testid={testid ? `${testid}-scroll` : undefined}>
          <StateBody state={state} reason={reason} small>{children}</StateBody>
        </div>
      ) : (
        <StateBody state={state} reason={reason} small>{children}</StateBody>
      )}
    </section>
  );
}

/** The period pills. Controlled: the parent owns the value. */
export const PERIODS = Object.freeze(['Today', 'Week', 'Month', 'Quarter', 'Year']);

function PeriodSwitch({ value, onChange }) {
  return (
    <div role="group" aria-label="Reporting period" style={{ display: 'flex', gap: 4 }}>
      {PERIODS.map((p) => {
        const on = p === value;
        return (
          <button
            key={p} type="button" onClick={() => onChange(p)}
            aria-pressed={on}
            data-testid={`period-${p.toLowerCase()}`}
            style={{
              padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 999,
              cursor: 'pointer', border: `1px solid ${on ? C.primary : C.border}`,
              background: on ? C.primary : C.card, color: on ? '#fff' : C.body,
            }}
          >{p}</button>
        );
      })}
    </div>
  );
}


/**
 * P185 item 1 — a custom From–To window, beside the preset pills.
 *
 * ⛔ INVALID INPUT NEVER REACHES THE SERVER. Apply refuses and says why; the
 * dashboard is not reloaded and no request is sent. A coercer would turn an
 * empty box into the epoch and a reversed pair into a negative window, and the
 * screen would still look fine.
 *
 * ⚠ The dates are BUSINESS-TIME and inclusive of both days — the parsing and
 * the boundary live in customRange(), once, shared with the server twin. This
 * control does no date arithmetic of its own.
 */
function CustomRangeControl({ value, onApply }) {
  const [open, setOpen] = React.useState(Boolean(value));
  const [from, setFrom] = React.useState(value ? value.fromYmd : '');
  const [to, setTo] = React.useState(value ? value.toYmd : '');
  const [err, setErr] = React.useState(null);

  const apply = () => {
    const r = customRange(from, to);
    if (!r.ok) { setErr(r.why); return; }
    setErr(null);
    onApply({ from: r.from.toISOString(), to: r.to.toISOString(), label: r.label, fromYmd: from, toYmd: to });
  };

  if (!onApply) return null;
  if (!open) {
    return (
      <button type="button" data-testid="custom-range-open" style={S.link}
              onClick={() => setOpen(true)}>Custom range</button>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
         data-testid="custom-range">
      <input type="date" value={from} aria-label="From date" data-testid="custom-range-from"
             onChange={(e) => setFrom(e.target.value)} style={S.dateInput} />
      <span style={{ fontSize: 12, color: C.muted }}>to</span>
      <input type="date" value={to} aria-label="To date" data-testid="custom-range-to"
             onChange={(e) => setTo(e.target.value)} style={S.dateInput} />
      <button type="button" data-testid="custom-range-apply" onClick={apply} style={S.applyBtn}>Apply</button>
      {/* ⭐ THE APPLIED WINDOW, IN WORDS. The two date inputs show what is
          TYPED, which is not the same thing — a reader who edits a box and
          does not press Apply would otherwise believe the dashboard had
          already moved. This says what the figures actually cover. */}
      {value && (
        <strong data-testid="custom-range-label" style={{ fontSize: 12, color: C.text }}>
          {value.label}
        </strong>
      )}
      {value && (
        <button type="button" data-testid="custom-range-clear" style={S.link}
                onClick={() => { setErr(null); onApply(null); }}>Clear</button>
      )}
      {/* ⚠ The reason, not just a red border — the message is the only thing
          that tells the user WHICH rule they broke. */}
      {err && <span role="alert" data-testid="custom-range-error"
                    style={{ fontSize: 11.5, color: C.highDeep }}>{err}</span>}
    </div>
  );
}

export default function CeoOverview({
  data, attention, attentionState, finance, financeState,
  /* P204 — the affiliate programme. */
  affiliate = null, affiliateState = 'loading',
  reps, repsState, stages, stagesState, period = 'Month', onPeriodChange = () => {},
  /* P185 item 1 — the custom reporting window. Null ⇒ a preset is active. */
  customRange = null, onCustomRange = null,
  /* P182 PR2 — opens the AI pipeline report. Absent (null) for anyone whose
     grant does not reach it, in which case the button is not rendered. */
  onOpenAiReport = null,
  /* ⭐ P229 — the way back to a closed report. */
  lastReportChip = null,
  /* ⭐ P230 — a job is running; this button must say so. */
  aiReportBusy = false,
  /* P196 — the graphics. `charts` is ONE payload for all three. */
  charts = null, chartsState = 'loading', chartPipeline = '',
  onChartPipelineChange = null, onDrillFunnel = null, onDrillMovement = null,
  onDrill = () => {}, canExport = false, onExport = () => {},
  onOpenAttentionItem = null, onShowAllAttention = null,
  onOpenTeamReport = null, onOpenRevenueReport = null, asOf = null, downloadMenu = null,
  /*
   * ⚠ A SEPARATE CALLBACK FROM onDrill, deliberately. onDrill opens an
   * OPPORTUNITY metric on the analytics registry; the receivables figures are
   * FINANCE metrics on a different registry with a different gate. Routing them
   * through one callback would mean the caller guessing which registry a key
   * belongs to from its name — and `ceo.booked_won_value` was already being
   * sent for "View invoices", which opens the wrong figure entirely.
   */
  onDrillFinance = null,
  /*
   * ⭐⭐ CARD P254 — THE GOAL TRACKER. One payload from reports:ceo:goal.
   *
   * ⚠ IT IS NOT UNDER THE PERIOD SWITCH, and that is deliberate. Every other
   * figure on this screen moves with the period control at the top; the goal
   * tracker is always the CURRENT MONTH, because "are we going to hit our
   * monthly revenue goal" is not a question about last quarter. Wiring it to
   * `period` would silently answer a different question whenever Carlos
   * changed the dropdown, and the card would still look right.
   */
  goal = null, goalState = 'loading',
  /* Null hides the "Set a monthly target" affordance — see the P167 rule in
     _OverviewCard.js: no dead controls. */
  canSetTarget = false, onSaveTarget = null,
  // P160 item 1 — opens one rep's own report. Null hides the affordance.
  onOpenRep = null, repOverdue = null,
}) {
  const fin = financeCards(finance, {
    loading: financeState === 'loading', failed: financeState === 'failed',
  });
  const summary = fin.summary;

  const coverage = data && data.pipeline && data.pipeline.coverage;

  /*
   * ⭐⭐ Every KPI now reads W3's metric envelope (#732), falling back to the
   * legacy scalar so this screen works against BOTH servers — see
   * _ceoMetrics.js. The legacy path reports `previous: null`, so no Δ chip
   * renders rather than a fabricated one.
   */
  const mOpenValue = metric(data, 'openValue', (d) => d.pipeline?.openValue, 'currency');
  const mBooked = metric(data, 'bookedWonValue', (d) => d.revenue?.bookedWon, 'currency');
  const mLeads = metric(data, 'leadsThisMonth', (d) => d.leadGen?.thisMonth, 'count');
  const mConv = metric(data, 'leadToDeposit', (d) => d.conversion?.leadToDeposit, 'percent');
  const mF30 = metric(data, 'forecast30d', (d) => d.forecast?.d30, 'currency');
  const mF60 = metric(data, 'forecast60d', (d) => d.forecast?.d60, 'currency');
  const mF90 = metric(data, 'forecast90d', (d) => d.forecast?.d90, 'currency');
  const prevLabel = previousLabel(data);
  const unmapped = unmappedStages(data);
  const forecastProvisional = [mF30, mF60, mF90].some((m) => m && m.provisional);

  /* ⭐ Finance now follows the period switch — #744. */
  const fCollected = financeMetric(summary, 'collected');
  const fInvoiced = financeMetric(summary, 'invoiced');
  const rangeLabel = financeRangeLabel(summary);
  const pit = pointInTimeNote(summary);

  /* Money vs count vs percent — the envelope says which; never guessed. */
  const show = (m) => {
    if (!m || !m.tracked) return null;
    // P161 item 4 — the envelope names its SCALE now. 'currency' named none,
    // and the two senders meant different things by it.
    if (m.unit === 'cents') return formatCents(m.value);
    if (m.unit === 'dollars' || m.unit === 'currency') return formatDollars(m.value);
    if (m.unit === 'percent') return `${m.value}%`;
    return Number(m.value).toLocaleString();
  };
  const repRows = useMemo(
    /*
     * ⛔⛔ THE ROSTER WINS WHEN IT LOADED (Card P187).
     *
     * `attention` is a FEED — capped at the top few reps by design — and using
     * it as the panel's row source turned a display limit into a product
     * decision: measured 30 Oct, ~1,128 overdue tasks (about 76% of all
     * overdue work) had no row to appear in. `repOverdue` is the uncapped
     * roster: every active rep, plus everyone holding open work, plus the
     * unassigned bucket the old INNER JOIN deleted.
     *
     * ⚠ FALLS BACK rather than blanking. A null roster (the call failed, or an
     * older preload has no bridge) keeps the previous behaviour — fewer rows
     * beats no panel — and the two are distinguishable because a failed roster
     * logs while an absent one does not.
     */
    () => mergeRepRows({
      performance: reps || [],
      overdue: Array.isArray(repOverdue) && repOverdue.length
        ? repOverdue
        : overdueRowsFromAttention(attention),
    }),
    // ⚠ repOverdue MUST be a dependency: without it the panel keeps the first
    // render's rows and the roster never appears, which looks exactly like the
    // bug this card fixes.
    [reps, attention, repOverdue]
  );

  /*
   * ⚠ The 12-month series comes from W2's summary when it carries one, and is
   * otherwise ABSENT — not an empty chart with a flat zero line, which reads
   * as "no revenue" rather than "no data".
   */
  const trend = (summary && Array.isArray(summary.monthly)) ? summary.monthly : null;

  /*
   * ⭐ THE TARGET LINE, MOVED HERE FROM THE DELETED CHART — Card P217 (B).
   *
   * ⛔⛔ UNITS. `charts.targets` is keyed by month and holds DOLLARS — the
   * deleted chart plotted dollars, converting with centsToDollars(). THIS
   * card plots `collectedCents`, in CENTS, straight onto its axis. Moving the
   * line without multiplying by 100 would draw a target one hundredth of its
   * real size, under a plausible-looking dashed line. A test pins the factor.
   *
   * ⚠ NO TARGET, NO LINE — never an invented one. An unset target draws
   * nothing and the caption says so, which is the rule the deleted chart
   * carried and the reason it is restated here rather than dropped.
   */
  const [trendView, setTrendView] = useChartView('chart');
  const trendRef = React.useRef(null);
  /* Card P242 B2a — chart is the default view on both panels. */
  const [stagesView, setStagesView] = useChartView('chart');
  const stagesRef = React.useRef(null);
  const [agingView, setAgingView] = useChartView('chart');
  const agingRef = React.useRef(null);

  /*
   * ⛔⛔ ADAPTED, NOT RE-DRAWN. CeoCharts already carries P228/P231's rules —
   * top 10 plus an "Other" CAPTION rather than a bar, per-row axis sizing,
   * labels suppressed on bars too short to hold them. Reusing it means those
   * rules apply here without a second copy to keep in step.
   *
   * ⚠ UNITS CHANGE AT THIS BOUNDARY: the Overview speaks cents, these charts
   * speak dollars. _panelAdapters.js owns that conversion and a test pins it.
   */
  const stageBarRows = React.useMemo(() => stagesToBarRows(stages), [stages]);

  const targets = charts && charts.targets;
  const trendWithTarget = React.useMemo(() => withTargetCents(trend, targets), [trend, targets]);

  const hasTarget = !!(trendWithTarget || []).some((r) => r.targetCents != null);

  const invoiceBuckets = summary && summary.invoices && summary.invoices.byBucket;
  const donut = invoiceBuckets ? [
    { key: 'paid', name: 'Paid', value: invoiceBuckets.paid?.n || 0, fill: C.primary },
    { key: 'due', name: 'Due', value: invoiceBuckets.due?.n || 0, fill: '#60A5FA' },
    { key: 'partially_paid', name: 'Partially paid (deposits)', value: invoiceBuckets.partially_paid?.n || 0, fill: C.watch },
    { key: 'overdue', name: 'Overdue', value: invoiceBuckets.overdue?.n || 0, fill: C.high },
    { key: 'draft', name: 'Draft', value: invoiceBuckets.draft?.n || 0, fill: C.faint },
  ].filter((d) => d.value > 0) : null;

  /*
   * ⭐⭐ THE BANDS AND THEIR WORDS COME FROM THE SERVER (card P256).
   *
   * This read `aging['1-30'] || aging.d1_30` — a defensive OR covering for the
   * fact that two spellings of one vocabulary were in play. src/pages/reports/
   * _reportModel.js carried only the FIRST half of that OR and so rendered $0
   * in all four bands of the PDF. The server now sends `agingRows` with the key,
   * the label and the order attached, and `overdue` on each row saying whether
   * it belongs in an overdue chart at all.
   *
   * ⛔ `.filter(r => r.overdue)` is what keeps "Not yet due" out of a chart
   * titled Overdue. It has its own figure below.
   */
  const aging = summary && summary.overdue && summary.overdue.aging;
  const serverRows = summary && summary.overdue && summary.overdue.agingRows;
  const agingRows = Array.isArray(serverRows) && serverRows.length
    ? serverRows.filter((r) => r.overdue).map((r) => ({ band: r.label, n: r.n, outstandingCents: r.outstandingCents }))
    : (aging ? [
        { band: '1–30 days', ...(aging.d1_30 || aging['1-30'] || {}) },
        { band: '31–60 days', ...(aging.d31_60 || aging['31-60'] || {}) },
        { band: '61–90 days', ...(aging.d61_90 || aging['61-90'] || {}) },
        { band: '90+ days', ...(aging.d90_plus || aging['90+'] || {}) },
      ] : null);
  /* ⚠ Declared AFTER agingRows — a memo above it is a temporal dead zone,
     which is how the first attempt broke every Overview test at once. */
  const agingBlock = React.useMemo(() => agingToBuckets(agingRows), [agingRows]);

  /* ── Card P242 B2b ──────────────────────────────────────────────────── */
  const [invoicesView, setInvoicesView] = useChartView('chart');
  const invoicesRef = React.useRef(null);
  const [sourcesView, setSourcesView] = useChartView('chart');
  const sourcesRef = React.useRef(null);

  const invoiceStack = React.useMemo(() => invoiceBucketsToStack(donut), [donut]);

  /*
   * ⚠ ONE FOLD, USED TWICE. The chart shows at most six slices (P222); the
   * table shows every source. Both come from the same function with a
   * different budget, so the "Other" on the chart is always exactly the rows
   * the table lists below it.
   */
  const leadSources = (data && data.leadSources) || null;
  const sourceChart = React.useMemo(() => leadSourcesToRows(leadSources, 6), [leadSources]);
  const sourceTable = React.useMemo(() => leadSourcesToRows(leadSources, 9999), [leadSources]);

  return (
    <div style={S.page} data-testid="ceo-overview" className="ceo-print-root">
      {/* ⚠ Print-only: a loose sheet must say what it is and when it was true. */}
      <div className="ceo-print-header" style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>ZignaGenix — CEO Report</div>
        <div style={{ fontSize: 11, color: C.muted }}>
          Period: {period} · As of {new Date(asOf || Date.now()).toISOString().slice(0, 19).replace('T', ' ')} UTC
        </div>
      </div>
      {/* ── Header ───────────────────────────────────────────────────── */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>CEO Overview</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="ceo-no-print" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <PeriodSwitch value={customRange ? null : period} onChange={(p) => { if (onCustomRange) onCustomRange(null); onPeriodChange(p); }} />
            <CustomRangeControl value={customRange} onApply={onCustomRange} />
          </span>
          {typeof onOpenAiReport === 'function' ? (
            <>
              {/*
                * ⛔ Card P230 — THE SAME STATE WHEREVER THE JOB CAN BE STARTED.
                * This button opens the dialog that starts the job, so while one
                * is running it must say so and must not start another — a reader
                * who closed the popup (P229) still has this button in front of
                * them and no other sign that the model is working.
                */}
              <button type="button" className="ceo-no-print"
                      style={{ ...S.link, fontWeight: 600 }}
                      data-testid="overview-ai-report"
                      disabled={aiReportBusy}
                      aria-busy={aiReportBusy ? 'true' : undefined}
                      onClick={onOpenAiReport}>
                {aiReportBusy ? 'Generating…' : 'AI report'}
                {aiReportBusy ? (
                  <span data-testid="overview-ai-report-sr" style={{
                    position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
                    overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
                  }}>Generating report</span>
                ) : null}
              </button>
              {/* ⛔ P229 — beside the button that made it, which is where a
                  reader looks for it. Renders nothing until one exists. */}
              {lastReportChip}
            </>
          ) : null}
          {downloadMenu}
          {canExport ? (
            <button type="button" style={{ ...S.link, fontWeight: 600 }}
                    data-testid="overview-export" onClick={onExport}>Export CSV</button>
          ) : null}
        </div>
      </header>

      {/* ── Row −1 · THE GOAL TRACKER (Card P254) ────────────────────────
        * ⛔ FIRST CONTENT BLOCK ON THE PAGE, above the graphics and the
        * tiles. Carlos's requirement is an answer in under 60 seconds, and a
        * figure that needs scrolling to reach has already failed it.
        *
        * ⚠ `leadsToday` is the SAME `data.leadGen.today` the New leads tile
        * below already renders — read once from the dashboard payload, not
        * counted again here, so the strip and the tile cannot disagree.
        */}
      <GoalTracker
        data={goal} state={goalState}
        canSetTarget={canSetTarget} onSaveTarget={onSaveTarget}
        onOpenCollected={onDrillFinance ? (() => onDrillFinance('finance.collected')) : null}
        onOpenBooked={onDrillFinance ? (() => onDrillFinance('finance.invoiced')) : null}
        onOpenLeads={onDrill ? (() => onDrill('ceo.new_leads')) : null}
        leadsToday={data && data.leadGen && Number.isFinite(Number(data.leadGen.today))
          ? Number(data.leadGen.today) : null}
        leadsTodayState={data ? 'ok' : 'loading'}
      />

      {/* ── Row 0 · Graphics (P196) ──────────────────────────────────────
        * ⚠ Placed BELOW the header and ABOVE the KPI tiles: Carlos asked to
        * see the shape first. The tiles stay exactly where they were, so
        * nobody has to relearn the page.
        * ⚠ A grid that collapses to one column on a phone — `auto-fit` with a
        * min width, so the charts shrink rather than the page scrolling
        * sideways.
        */}
      <div
        data-testid="ceo-charts-row"
        style={{
          display: 'grid', gap: 12, marginBottom: 16,
          /*
           * ⚠ Card P208: 320px, not 280. At 1280-1440px this gives three
           * columns; below ~1100px it falls to two and then to one, and a
           * chart card never gets narrower than its axis gutter plus a
           * readable plot. auto-fit does the breakpoints without a media
           * query, so there is nothing to keep in step with the CSS.
           */
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          /* ⛔ Grid items default to stretch; `start` lets each card be its
             own height so a tall funnel note cannot stretch its neighbours
             and cannot be clipped by a row height it did not ask for. */
          alignItems: 'start',
        }}
      >
        {chartsState === 'failed' ? (
          <div data-testid="charts-failed"
               style={{ ...S.card, color: C.highDeep, fontSize: 12 }}>
            ⚠ The charts could not be loaded. The figures in the tiles below are
            unaffected.
          </div>
        ) : chartsState === 'untracked' ? (
          <div data-testid="charts-untracked"
               style={{ ...S.card, color: C.muted, fontSize: 12 }}>
            Charts are not available in this build.
          </div>
        ) : charts ? (
          <>
            {/*
              * P196 item 3 — the pipeline picker, reusing the inventory P182
              * already loads (reports:ceo:pipelines). ⚠ It scopes the FUNNEL
              * and the WATERFALL; revenue is collected payments and has no
              * pipeline dimension, which the label says rather than leaving
              * the reader to assume the filter applied to all three.
              */}
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center',
                          gap: 8, flexWrap: 'wrap' }} className="ceo-no-print">
              <label style={{ fontSize: 12, fontWeight: 600, color: C.text }}
                     htmlFor="chart-pipeline">Pipeline</label>
              <select
                id="chart-pipeline"
                data-testid="charts-pipeline"
                value={chartPipeline || ''}
                onChange={(e) => onChartPipelineChange && onChartPipelineChange(e.target.value)}
                style={{ fontSize: 12, padding: '4px 6px', border: `1px solid ${C.border}`,
                         borderRadius: 6, background: C.card, color: C.text }}
              >
                <option value="">All pipelines</option>
                {(charts.pipelines || []).map((p) => (
                  <option key={p.pipeline_id} value={String(p.pipeline_id)}>
                    {p.pipeline} ({p.open_opps})
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: C.muted }}>
                Scopes the funnel and the movement bars. Revenue is collected
                payments and is not affected.
              </span>
            </div>
            {/*
              * ⛔⛔ "Revenue over time" IS GONE FROM THIS ROW — Card P217 (B),
              * Daniel's call.
              *
              * P207 made it the SAME SERIES as "Collected revenue — 12
              * months" below: one query, collectedMonthlySeries(). Two cards
              * drawing one series is not redundancy a reader can ignore — it
              * invites the question "why do these differ?" every time a
              * rounding or a window makes them look unlike each other, and
              * the honest answer would be that they cannot.
              *
              * ⭐ The Collected card is the survivor because it OWNS THE
              * FORECAST, which this one never had. The optional target line
              * moved there with it.
              *
              * ⚠ The RevenueChart COMPONENT stays in CeoCharts.js — the AI
              * report renders it for its `trend` chart type
              * (CeoReportView.js:135). Deleting it would break that.
              */}
            <MovementWaterfall
              movement={charts.movement}
              basis={charts.basis}
              onDrill={onDrillMovement}
            />
            <SalesFunnel
              funnel={charts.funnel}
              unmappedStages={charts.unmappedStages}
              winRate={charts.winRate}
              terminalBuckets={charts.terminalBuckets}
              onDrill={onDrillFunnel}
            />
          </>
        ) : null}
      </div>

      {/*
        * ── The affiliate programme (Card P204) ──────────────────────────
        * ⚠ Rendered even when it FAILED, because the panel's failed state is
        * the message "these figures are unavailable; they are not zero".
        * Hiding it on failure would leave the page looking complete while a
        * whole revenue channel was silently missing from it.
        */}
      <div style={S.row} data-print-section>
        <AffiliatePanel data={affiliate} state={affiliateState} C={C}
                        /* ⚠ Same one-query figure the Collected tile uses, so
                           the panel and the tile cannot disagree. */
                        shareOfCollected={affiliateSubLine(summary, formatCents)} />
      </div>

      {/* ── Row 1 · KPI cards ────────────────────────────────────────── */}
      <div style={{ ...S.row, ...S.kpiRow }} data-print-section data-print-keep>
        <OverviewCard
          testid="kpi-collected" label="Collected"
          onOpen={onDrillFinance ? (() => onDrillFinance('finance.collected')) : null}
          state={fin.state === 'ok' ? cardState(fCollected) : fin.state}
          reason={fin.state === 'ok' ? reasonText(fCollected) : fin.reason}
          /* ⛔ formatCents: the finance envelope's currency is CENTS, unlike
             the ceo envelope's dollars — see _reportModel.js. */
          value={fCollected.tracked ? formatCents(fCollected.value)
                 : (summary && summary.collected ? formatCents(summary.collected.cents) : null)}
          deltaPct={fCollected.deltaPct}
          deltaLabel={financePreviousLabel(summary, fCollected) || 'vs previous'}
          /*
           * ⭐⭐ THE LABEL COMES FROM THE SERVER'S RANGE — #744 / P158.
           * It used to read a hard-coded "All time", which was honest while
           * the API ignored the period and becomes a LIE the moment it stops.
           * The figure and the words describing it now come from the same
           * decision about which window was used.
           */
          sub={summary && summary.tracked !== false ? [
            rangeLabel,
            previousReasonText(fCollected),
            fCollected.provisional ? 'Provisional — partial refunds still syncing.' : null,
            /*
             * ⚠ Card P220 Part A. Absent rather than "$0.00 (0%)" when the
             * slice is untracked — a zero there would claim the affiliate
             * channel earned nothing.
             */
            affiliateSubLine(summary, formatCents),
            MONEY_SUB,
          ].filter(Boolean).join(' · ') : null}
        />
        <OverviewCard
          testid="kpi-booked" label="Booked (won deals)"
          onOpen={onDrill ? (() => onDrill('ceo.booked_won_value')) : null}
          state={cardState(mBooked, { loading: !data })} reason={reasonText(mBooked)}
          value={show(mBooked)} deltaPct={mBooked.deltaPct} deltaLabel={prevLabel}
          sub="Won-deal values in the CRM — not cash received"
        />
        <OverviewCard
          testid="kpi-pipeline" label="Active pipeline"
          onOpen={onDrill ? (() => onDrill('ceo.open_pipeline_value')) : null}
          state={cardState(mOpenValue, { loading: !data })} reason={reasonText(mOpenValue)}
          value={show(mOpenValue)} deltaPct={mOpenValue.deltaPct} deltaLabel={prevLabel}
          sub={coverage
            ? `Only ${coverage.valued} of ${coverage.total} deals carry a value`
            : 'Excludes old, cold and long-term nurture stages'}
        />
        <OverviewCard
          testid="kpi-leads" label="New leads"
          /* ⭐ P179 item 3 — this tile opened NOTHING. It now drills to the
             leads it counts; the metric deliberately mirrors the tile's own
             definition, is_active quirk included. */
          onOpen={onDrill ? (() => onDrill('ceo.new_leads')) : null}
          state={cardState(mLeads, { loading: !data })} reason={reasonText(mLeads)}
          value={show(mLeads)}
          sub={data ? `Today ${data.leadGen?.today ?? 0} · This week ${data.leadGen?.thisWeek ?? 0}` : null}
          /* ⚠ The server's Δ when it sends one; the local computation only as
             a fallback for the legacy payload, never both. */
          deltaPct={mLeads.deltaPct !== null && mLeads.deltaPct !== undefined
            ? mLeads.deltaPct
            : deltaPct(data?.leadGen?.thisMonth, data?.leadGen?.previousMonth)}
          deltaLabel={prevLabel}
        />
        <OverviewCard
          testid="kpi-conversion" label="Lead → Deposit"
          state={cardState(mConv, { loading: !data })} reason={reasonText(mConv)}
          value={show(mConv)} deltaPct={mConv.deltaPct} deltaLabel={prevLabel}
          sub={data && data.conversion?.approximated ? 'Approximate before 17 Jul 2026' : null}
        />
      </div>

      {/*
        * ⭐⭐ STAGES NOBODY HAS CLASSIFIED. W3 counts an unmapped stage as
        * ACTIVE and reports it rather than dropping it — silently excluding
        * what cannot be classified is how a pipeline total shrinks for
        * reasons nobody can see. Surfacing the names keeps the outstanding
        * ruling visible instead of buried in a payload.
        */}
      {unmapped ? (
        <div data-testid="unmapped-stages" style={{
          ...S.card, marginBottom: 16, borderColor: '#FDE68A', background: '#FFFBEB',
          fontSize: 12, color: C.watchDeep,
        }}>
          <strong>{unmapped.count} stage{unmapped.count === 1 ? '' : 's'} need Carlos&rsquo;s ruling.</strong>{' '}
          Counted as active pipeline until classified
          {unmapped.names.length ? <>: {unmapped.names.slice(0, 8).join(', ')}
            {unmapped.names.length > 8 ? ` and ${unmapped.names.length - 8} more` : ''}</> : null}.
        </div>
      ) : null}

      {/*
        * ── Lead sources (Card P242 B2b, item 11) — A NEW PANEL ──────────
        *
        * ⚠ The Overview never had one. `data.leadSources` has been in the
        * payload all along (routes/_ceo_dashboard.js:541) and only the AI
        * report drew it, so the question "where are leads coming from" had
        * no answer on the screen Carlos actually reads.
        */}
      {sourceChart ? (
        <div style={S.row} data-print-section>
          <Panel title="Lead sources" testid="panel-sources" state="ok">
            <PanelActions view={sourcesView} onChange={setSourcesView} testid="panel-sources"
                          panelRef={sourcesRef}
                          meta={{ panel: 'Lead sources', title: 'Lead sources',
                                  period: rangeLabel }} />
            {sourcesView === 'chart' ? (
              <div data-testid="panel-sources-well" ref={sourcesRef}>
                {/* ⚠ At most six slices; the rest collapse into a named
                    "Other (n)" — the same fold the table below lists in full. */}
                <SourceDonut data={sourceChart.rows} label="Lead sources" maxSlices={99} />
              </div>
            ) : (
              <table data-testid="panel-sources-table"
                     style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ textAlign: 'left' }}>Source</th>
                    <th scope="col" style={{ textAlign: 'right' }}>Leads</th>
                    <th scope="col" style={{ textAlign: 'right' }}>Share</th>
                    <th scope="col" style={{ textAlign: 'right' }}>Previous</th>
                  </tr>
                </thead>
                <tbody>
                  {(sourceTable ? sourceTable.rows : []).map((r) => (
                    <tr key={r.source} data-testid={`source-row-${r.source}`}>
                      <td>{r.source}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.count.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: C.muted }}>
                        {Math.round((r.count / sourceTable.total) * 1000) / 10}%
                      </td>
                      {/*
                        * ⛔⛔ THE PREVIOUS COUNT, NOT A GROWTH PERCENTAGE.
                        * P237 requires a comparison to state its window and a
                        * suppressed % to give its reason; a raw count pair
                        * cannot say whether the windows are comparable, so
                        * the number is shown and no % is invented.
                        */}
                      <td style={{ textAlign: 'right', color: C.faint }}>
                        {r.previousCount === null ? '—' : r.previousCount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div data-testid="panel-sources-note" style={{ ...S.kpiSub, marginTop: 8 }}>
              {sourceChart.folded
                ? `Top ${sourceChart.rows.length - 1} shown; ${sourceChart.folded} more in "Other" — see Table.`
                : 'All sources shown.'}
              {' '}Counts are contacts created in {rangeLabel}.
            </div>
          </Panel>
        </div>
      ) : null}

      {/* ── Row 2 · trend | invoice status ───────────────────────────── */}
      <div style={{ ...S.row, ...S.twoUp }} data-print-section>
        <Panel
          title="Collected revenue — 12 months + forecast"
          action="View full report →" onAction={onOpenRevenueReport || undefined}
          testid="panel-trend"
          state={fin.state === 'ok' ? (trend ? 'ok' : 'untracked') : fin.state}
          reason={fin.state === 'ok' && !trend
            ? 'A month-by-month series is not in the finance summary yet.'
            : fin.reason}
        >
          <PanelActions view={trendView} onChange={setTrendView} testid="panel-trend"
                        panelRef={trendRef}
                        meta={{ panel: 'Collected revenue', title: 'Collected revenue',
                                period: rangeLabel }} />
          {trendView === 'table' ? (
            <table data-testid="panel-trend-table"
                   style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th scope="col" style={{ textAlign: 'left' }}>Month</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Direct</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Affiliate</th>
                  <th scope="col" style={{ textAlign: 'right' }}>Collected</th>
                </tr>
              </thead>
              <tbody>
                {(trendWithTarget || []).map((r) => (
                  <tr key={r.month} data-testid={`panel-trend-row-${r.month}`}>
                    <td>{r.month}</td>
                    <td style={{ textAlign: 'right' }}>{formatCents(r.directCents || 0)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCents(r.affiliateCents || 0)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatCents(r.collectedCents || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
          <ChartWell height={180} testid="panel-trend-well">
            <div ref={trendRef} style={{ width: '100%', height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              {/*
                * ⭐⭐ STACKED COLUMNS, Direct vs Affiliate — Card P220 Part A.
                *
                * ⛔ The two Bars share stackId="c", so what the reader sees is
                * the FULL Collected column with the affiliate share shown as a
                * band inside it. They are server-measured and pinned to sum to
                * `collectedCents`, so the stack height IS the Collected line
                * this card drew before — the breakdown changed, the total did
                * not.
                *
                * ⚠ Bars, not an area: a stacked AREA invites reading the
                * boundary as a trend line in its own right, which it is not.
                */}
              <ComposedChart data={trendWithTarget || []} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={C.borderSoft} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.faint }} axisLine={false}
                       tickLine={false} minTickGap={8} />
                {/* ⚠ Axes start at 0 — a truncated money axis exaggerates
                    every movement on it. */}
                <YAxis tick={{ fontSize: 11, fill: C.faint }} axisLine={false} tickLine={false}
                       width={48} domain={[0, 'auto']} />
                <Tooltip formatter={(v) => formatCents(Number(v) || 0)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {/*
                  * ⭐ Card P222 item 3 — WCAG 1.4.11 is met by the BOUNDARY,
                  * not the fill. Each bar carries a 1px outline in a darkened
                  * shade of its own hue, ≥3:1 against the white well, so the
                  * published Okabe-Ito fills can stay exactly as designed.
                  * The Legend above names both series, so colour is never the
                  * only signal (1.4.1).
                  */}
                <Bar dataKey="directCents" stackId="c" fill={C.seriesDirect}
                     stroke={C.seriesDirectInk} strokeWidth={1}
                     name="Direct" isAnimationActive={false} />
                <Bar dataKey="affiliateCents" stackId="c" fill={C.seriesAffiliate}
                     stroke={C.seriesAffiliateInk} strokeWidth={1}
                     name="Affiliate-referred" isAnimationActive={false} />
                {/* ⚠ Forecast: the SAME hue as Direct, dashed — P222 item 6.
                    A separate hue would read as another category. */}
                <Line type="monotone" dataKey="forecastCents" stroke={C.seriesDirectInk}
                      strokeWidth={2} strokeDasharray={FORECAST_DASH} dot={false}
                      name="Forecast" />
                {/*
                  * ⚠ A DIFFERENT COLOUR, not just a different dash. The
                  * forecast above is already dashed in the primary colour, so
                  * a second dashed primary line would be two things that look
                  * the same and mean different things. C.watch separates them,
                  * and both are named for the tooltip and the legend.
                  */}
                {hasTarget ? (
                  <Line type="monotone" dataKey="targetCents" stroke={C.watch} strokeWidth={2}
                        strokeDasharray="2 3" dot={false} name="Target" isAnimationActive={false} />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
            </div>
          </ChartWell>
          )}
          {/*
            * ⚠⚠ THE FORECAST SAYS WHAT IT RESTS ON. 18 stage spellings still
            * take the default 0.20 / 90-day weight pending Carlos's ruling,
            * which is why the 60-day figure reads low. A dashed line with no
            * caption would present an estimate of an estimate as a projection.
            */}
          {/*
            * ⛔⛔ THE SERVER DECIDES, NOT THIS FILE. The caption used to be
            * unconditional, which was right while every forecast was
            * provisional and becomes a LIE the moment one is not. W3's
            * envelope carries `provisional` per metric; when Carlos rules and
            * the flag clears, the warning disappears on its own rather than
            * waiting for someone to remember to delete it.
            *
            * ⚠ Legacy payloads carry no flag, so nothing is claimed either
            * way — no caption rather than a stale one.
            */}
          {/*
            * ⚠ THE TARGET RULE TRAVELLED WITH THE LINE — Card P217 (B). The
            * deleted chart said on its face whether a target was configured;
            * dropping that sentence would leave a reader unable to tell "no
            * target set" from "target met exactly".
            */}
          <div data-testid="trend-target-note" style={{ ...S.kpiSub, marginTop: 8 }}>
            {hasTarget
              ? 'Short-dashed line is the configured target.'
              : 'No target configured, so none is drawn.'}
          </div>
          {forecastProvisional ? (
            <div data-testid="forecast-provisional" style={{ ...S.kpiSub, marginTop: 8, color: C.watchDeep }}>
              Forecast is <strong>provisional</strong> — waiting on Carlos&rsquo;s stage odds.
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
            {[['30 days', mF30], ['60 days', mF60], ['90 days', mF90]].map(([label, m]) => (
              <div key={label} data-testid={`forecast-${label.split(' ')[0]}`} style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11, color: C.muted }}>
                  Forecast {label}{m && m.provisional ? ' · provisional' : ''}
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                  {m && m.tracked ? formatDollars(m.value) : '—'}
                </div>
              </div>
            ))}
          </div>
        </Panel>

        {/* ⚠ WAS onDrill('ceo.booked_won_value') — an OPPORTUNITY metric behind
            a panel of INVOICE counts, so "View invoices" opened won deal value.
            Now the finance figure it actually describes. */}
        <Panel title="Invoice status"
               action={onDrillFinance ? 'View invoices →' : undefined}
               onAction={onDrillFinance ? (() => onDrillFinance('finance.invoiced')) : undefined}
               testid="panel-invoices"
               state={fin.state === 'ok' ? (donut && donut.length ? 'ok' : 'untracked') : fin.state}
               reason={fin.state === 'ok' ? 'No invoices have been mirrored yet.' : fin.reason}>
          <PanelActions view={invoicesView} onChange={setInvoicesView} testid="panel-invoices"
                        panelRef={invoicesRef}
                        meta={{ panel: 'Invoice status', title: 'Invoice status',
                                period: rangeLabel }} />
          {invoicesView === 'chart' && invoiceStack ? (
            <div data-testid="panel-invoices-well" ref={invoicesRef}>
              {/*
                * ⭐ Card P242 B2b — a 100% STACKED BAR. The donut answered
                * "how many of each"; this answers "what share is in trouble",
                * which is the question asked of invoice status.
                *
                * ⛔ THE TOTAL IS PRINTED. "16% overdue" of 50 invoices and of
                * 5,000 are different facts, and a share with no base is
                * unreadable.
                *
                * ⚠ Colours are SEMANTIC here (overdue red, draft grey), not
                * the categorical ramp — P222 keeps the two sets apart.
                */}
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
                {invoiceStack.total.toLocaleString()} invoice{invoiceStack.total === 1 ? '' : 's'} · {rangeLabel}
              </div>
              <div style={{ height: 64, background: C.well, border: `1px solid ${C.border}`,
                            borderRadius: 6, padding: 4, boxSizing: 'border-box' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={[invoiceStack.datum]}
                            margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                    <XAxis type="number" hide domain={[0, invoiceStack.total]} />
                    <YAxis type="category" hide dataKey="name" />
                    <Tooltip formatter={(v, k) => {
                      const hit = invoiceStack.series.find((x) => x.key === k);
                      return [`${v} (${hit ? hit.pct : 0}%)`, hit ? hit.name : k];
                    }} />
                    {invoiceStack.series.map((x) => (
                      <Bar key={x.key} dataKey={x.key} stackId="inv" name={x.name}
                           fill={x.fill} stroke={C.rule} strokeWidth={1}
                           isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* ⛔ Colour is never the only signal (1.4.1): every segment is
                  named with its share in the legend below. */}
              <ul data-testid="invoice-legend"
                  style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, fontSize: 12 }}>
                {invoiceStack.series.map((x) => (
                  <li key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}>
                    <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 2,
                                                      background: x.fill, flex: '0 0 auto' }} />
                    <span style={{ color: C.body }}>{x.name}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: C.text }}>{x.value}</span>
                    <span style={{ width: 54, textAlign: 'right', color: C.muted }}>{x.pct}%</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 150, height: 150, flex: '0 0 auto' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut || []} dataKey="value" nameKey="name" innerRadius={44} outerRadius={70} paddingAngle={2}>
                    {(donut || []).map((d) => <Cell key={d.key} fill={d.fill} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 12, flex: 1, minWidth: 0 }}>
              {(donut || []).map((d) => (
                <li key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                  <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: 2, background: d.fill, flex: '0 0 auto' }} />
                  <span style={{ color: C.body }}>{d.name}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700, color: C.text }}>{d.value}</span>
                </li>
              ))}
            </ul>
          </div>
          )}
        </Panel>
      </div>

      {/* ── Row 3 · exceptions | team ────────────────────────────────── */}
      <div style={{ ...S.row, ...S.twoUp }} data-print-section>
        <Panel title="Exceptions requiring attention"
               action={attention && attention.items ? `View all (${attention.items.length}) →` : null}
               /* ⚠ The FULL panel, which already exists on the detail view with
                  every item and the not-tracked list — not an opportunity
                  drill-down, which is a different question entirely. */
               onAction={onShowAllAttention || undefined}
               testid="panel-exceptions"
               notAffected={customRange ? 'these are open items as of now' : null}
               state={attentionState === 'failed' ? 'failed' : attentionState === 'loading' ? 'loading' : 'ok'}
               reason={attentionState === 'failed' ? 'Needs attention could not be loaded.' : null}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {/* ⛔ P179 — `.slice(0, 5)` REMOVED. The server already ranks and
                caps these (ATTENTION_CONFIG.maxItems); slicing again here threw
                away rows it had deliberately kept, and the panel gave no sign.
                The panel scrolls instead. */}
            {((attention && attention.items) || []).map((it, i) => (
              <li key={it.key || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i ? `1px solid ${C.borderSoft}` : 'none' }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                  color: i < 2 ? C.highDeep : C.watchDeep,
                  background: i < 2 ? C.highBg : '#FFFBEB',
                }}>{i < 2 ? 'HIGH' : 'WATCH'}</span>
                <span style={{ fontSize: 12.5, color: C.body, minWidth: 0 }}>{it.title}</span>
                {/*
                  * ⛔⛔ THE ITEM'S OWN LINK, NOT `it.key`. On v2.1.12 all five
                  * of these did nothing, because the attention item has NO
                  * `key` field — routes/_ceo_attention.js emits
                  * `{ruleKey, title, count, dollarsAtRisk, link}`. `it.key`
                  * was undefined, so the drill opened an unknown metric and
                  * rendered nothing. The item already carries its target in
                  * `link` ({view, filter}), and the page already knows how to
                  * follow it — openAttentionItem has existed since P140.
                  */}
                <button type="button" style={{ ...S.link, marginLeft: 'auto', flex: '0 0 auto' }}
                        data-testid={`exception-view-${i}`}
                        onClick={() => (onOpenAttentionItem ? onOpenAttentionItem(it) : onDrill(it.ruleKey))}>
                  View →
                </button>
              </li>
            ))}
            {attention && attention.items && attention.items.length === 0 ? (
              <li style={{ fontSize: 12.5, color: C.muted }}>Nothing needs attention right now.</li>
            ) : null}
          </ul>
          {/* ⚠ The not-tracked rules stay VISIBLE and separate — never ranked
              in among real items, and never silently dropped. */}
          {((attention && attention.notTracked) || []).map((nt) => (
            <div key={nt.key} style={{ ...S.kpiSub, marginTop: 8 }}>
              <strong style={{ color: C.muted }}>{nt.label}:</strong> {nt.message}
            </div>
          ))}
        </Panel>

        <Panel title="Team — overdue follow-ups by rep" action="View team report →"
               onAction={onOpenTeamReport || undefined} testid="panel-team"
               /*
                * ⭐ P185 — W3's roster (#765) is deliberately UNWINDOWED: a task
                * is overdue AS OF NOW, not "overdue during September". Scoping
                * it to a reporting window would produce a number nobody could
                * act on, so it is labelled rather than quietly left behind.
                */
               notAffected={customRange ? 'a task is overdue as of now, not during a past window' : null}
               state={repsState === 'failed' ? 'failed' : repsState === 'loading' ? 'loading' : 'ok'}
               reason={repsState === 'failed' ? 'The team report could not be loaded.' : null}>
          {/*
            * ⚠⚠ CSS BARS, NOT A CHART LIBRARY, AND DELIBERATELY.
            *
            * A recharts category axis draws its labels inside the SVG, where
            * a screen reader does not reach them and a test can only assert
            * on pixels. These rows are real text with a bar beside them: the
            * name is readable, selectable and assertable, and the bar is a
            * div whose width is the number. Same picture, honest markup.
            */}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="team-rows">
            {repRows.map((r) => {
              const max = Math.max(1, ...repRows.map((x) => x.overdue || 0));
              const pct = Math.round(((r.overdue || 0) / max) * 100);
              return (
                <li key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}
                    data-testid={`team-row-${r.repId == null ? 'unassigned' : r.repId}`}>
                  {/* P160 item 1 — the rep's name opens that rep's own report.
                      A button only when it leads somewhere; otherwise plain text. */}
                  {onOpenRep ? (
                    <button type="button" onClick={() => onOpenRep(r)}
                      data-testid={`team-open-${r.repId == null ? 'unassigned' : r.repId}`}
                      style={{ width: 170, flex: '0 0 auto', fontSize: 11.5, textAlign: 'left',
                               background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                               font: 'inherit', textDecoration: 'underline',
                               color: r.unassigned ? C.muted : C.primary }}>
                      {r.label}
                    </button>
                  ) : (
                    <span style={{ width: 170, flex: '0 0 auto', fontSize: 11.5, color: r.unassigned ? C.muted : C.body }}>
                      {r.label}
                    </span>
                  )}
                  <span aria-hidden="true" style={{ flex: 1, minWidth: 0, height: 10, background: C.borderSoft, borderRadius: 3 }}>
                    <span style={{ display: 'block', width: `${pct}%`, height: '100%', borderRadius: 3,
                                   background: r.unassigned ? C.faint : C.primary }} />
                  </span>
                  <span style={{ width: 54, textAlign: 'right', fontSize: 12, fontWeight: 700, color: C.text }}>
                    {(r.overdue || 0).toLocaleString()}
                  </span>
                </li>
              );
            })}
            {repRows.length === 0 ? (
              <li style={{ ...S.kpiSub }}>No overdue follow-ups.</li>
            ) : null}
          </ul>
        </Panel>
      </div>

      {/* ── Row 4 · receivables | pipeline stages ────────────────────── */}
      <div style={{ ...S.row, ...S.twoUp }} data-print-section>
        <Panel title="Receivables"
               action={onDrillFinance ? 'View aging report →' : undefined}
               onAction={onDrillFinance ? (() => onDrillFinance('finance.overdue')) : undefined}
               testid="panel-receivables"
               state={fin.state === 'ok' ? 'ok' : fin.state} reason={fin.reason}>
          {summary ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
                {/* ⚠ Clickable only when a finance drill-down is available —
                    offering a press that leads nowhere is the mistake the
                    referral queue's Promote button already avoids. */}
                <Figure label="Overdue" value={formatCents(summary.overdue.outstandingCents)}
                        sub={`${summary.overdue.count} invoices`} tone={C.high} testid="rec-overdue"
                        onClick={onDrillFinance ? (() => onDrillFinance('finance.overdue')) : undefined} />
                <Figure label="Deposits taken" value={formatCents(summary.deposits.receivedCents)}
                        sub={`${summary.deposits.count} partially paid`} testid="rec-deposits"
                        onClick={onDrillFinance ? (() => onDrillFinance('finance.deposits')) : undefined} />
              </div>
              <PanelActions view={agingView} onChange={setAgingView} testid="panel-receivables"
                            panelRef={agingRef}
                            meta={{ panel: 'Receivables aging',
                                    title: 'Unpaid invoices by age',
                                    period: rangeLabel }} />
              {agingView === 'chart' && agingBlock ? (
                <div data-testid="panel-receivables-well" ref={agingRef} style={{ width: '100%' }}>
                    {/* ⚠ Age runs one way: the adapter preserves band order and
                        never sorts by value. */}
                    <ReceivablesAging data={agingBlock} label="Unpaid invoices by age" />
                </div>
              ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 12 }} data-testid="rec-aging">
                {(agingRows || []).map((r) => (
                  <li key={r.band} style={{ display: 'flex', gap: 8, padding: '4px 0' }}
                      data-testid={`rec-aging-${r.band}`}
                      role={onDrillFinance ? 'button' : undefined}
                      onClick={onDrillFinance ? (() => onDrillFinance('finance.overdue', r.band)) : undefined}
                      style={{ display: 'flex', gap: 8, padding: '4px 0', cursor: onDrillFinance ? 'pointer' : undefined }}>
                    <span style={{ color: C.muted, width: 92 }}>{r.band}</span>
                    <span style={{ fontWeight: 700, color: C.text }}>{formatCents(r.outstandingCents || 0)}</span>
                    <span style={{ marginLeft: 'auto', color: C.faint }}>{r.n || 0}</span>
                  </li>
                ))}
              </ul>
              )}
              {/*
                * ⛔⛔ TWO CLOCKS, SAID OUT LOUD. Every figure above this panel
                * is a total for the selected period; Overdue and outstanding
                * deposits are a SNAPSHOT AS OF NOW and do not move when the
                * period changes. Side by side without a word, a reader takes
                * "Overdue $1.39M" as "overdue this month" — which it is not.
                * The server names these in pointInTime.appliesTo.
                */}
              {pit ? (
                <div data-testid="point-in-time-note" style={{ ...S.kpiSub, marginTop: 8, color: C.watchDeep }}>
                  {pit.note}
                </div>
              ) : null}
              {summary.overdue.noDueDate && summary.overdue.noDueDate.count > 0 ? (
                /* ⚠ Owed but un-ageable. Folding these into a band would
                   invent a due date; omitting them would lose the money. */
                <div style={{ ...S.kpiSub, marginTop: 8 }}>
                  {summary.overdue.noDueDate.count} invoices have no due date
                  ({formatCents(summary.overdue.noDueDate.outstandingCents)}) and cannot be aged.
                </div>
              ) : null}
            </>
          ) : null}
        </Panel>

        <Panel title="Where the open pipeline sits — top stages"
               action="Group by stage · rep · source →" onAction={() => onDrill('ceo.open_pipeline_value')}
               testid="panel-stages"
               state={stagesState || 'loading'}
               reason={stagesState === 'untracked'
                 ? 'Opening a figure requires the drill-down permission.'
                 : stagesState === 'failed' ? 'The stage breakdown could not be loaded.' : null}>
          <PanelActions view={stagesView} onChange={setStagesView} testid="panel-stages"
                        panelRef={stagesRef}
                        meta={{ panel: 'Open pipeline by stage',
                                title: 'Where the open pipeline sits',
                                period: rangeLabel }} />
          {/* ⚠ No ChartWell here: BarByStage carries its own card, and its plot
              area is already the white well (CeoCharts' Panel) — wrapping it
              again would nest a grey box inside a white one. */}
          {stagesView === 'chart' ? (
            <div data-testid="panel-stages-well" ref={stagesRef} style={{ width: '100%' }}>
                {/* ⚠ Height comes from the ROW COUNT inside the chart
                    (barChartHeight), not from a constant — 40 stages must not
                    become 40 one-pixel bars. */}
                <BarByStage data={stageBarRows} label="Open value by stage"
                            onDrill={onDrill ? ((k) => onDrill('ceo.open_value', k)) : null} />
            </div>
          ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 12.5 }} data-testid="stage-rows">
            {/* ⛔ P179 — `.slice(0, 6)` REMOVED, same reason: six stages shown
                out of however many exist, with nothing saying so. */}
            {(stages || []).map((g, i) => (
              <li key={g.key === null ? '__null__' : g.key}
                  style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
                {/* ⚠ A null stage is a real group — deals with no stage set —
                    and is labelled, never dropped or shown as blank. */}
                <span style={{ color: C.body, minWidth: 0 }}>
                  {g.key === null ? '(no stage set)' : g.key}
                </span>
                <span style={{ marginLeft: 'auto', fontWeight: 700, color: C.text }}>
                  {Number(g.n || 0).toLocaleString()}
                </span>
                <span style={{ width: 92, textAlign: 'right', color: C.muted }}>
                  {formatCents(g.totalCents || 0)}
                </span>
              </li>
            ))}
            {stages && stages.length === 0 ? (
              <li style={{ ...S.kpiSub }}>No open deals in any stage.</li>
            ) : null}
          </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function Figure({ label, value, sub, tone, testid, onClick }) {
  /*
   * ⚠ A BUTTON only when it leads somewhere. A Figure with no onClick stays a
   * div — offering a press that does nothing is worse than offering none.
   */
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag data-testid={testid} onClick={onClick} type={onClick ? 'button' : undefined}
      style={onClick ? { textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' } : undefined}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: tone || C.text, marginTop: 2 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{sub}</div> : null}
    </Tag>
  );
}
